import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../taskpane";

const ACTION_STYLES = {
  LOGIN: { bg: "#1e4a8a", label: "LOGIN" },
  CREATE_PROJECT: { bg: "#1a6e3a", label: "CREATE PROJECT" },
  DELETE_PROJECT: { bg: "#8a1e1e", label: "DELETE PROJECT" },
  UPDATE_PROJECT: { bg: "#2a6a4a", label: "UPDATE PROJECT" },
  CREATE_BOQ_VERSION: { bg: "#3a6a1a", label: "BOQ VERSION" },
  UPDATE_BOQ_VERSION: { bg: "#4a7a2a", label: "BOQ UPDATE" },
  DELETE_BOQ_VERSION: { bg: "#7a4a1a", label: "BOQ DELETE" },
  CREATE_CUSTOMER: { bg: "#0d5f6e", label: "CUSTOMER" },
  UPDATE_CUSTOMER: { bg: "#1a7a6a", label: "CUST UPDATE" },
  DELETE_CUSTOMER: { bg: "#7a1e4a", label: "CUST DELETE" },
  CREATE_USER: { bg: "#2e5a8a", label: "CREATE USER" },
  DELETE_USER: { bg: "#8a2e2e", label: "DELETE USER" },
  CATALOG_IMPORT: { bg: "#7a4a00", label: "CATALOG IMP" },
  PRICE_LIST_UPLOAD: { bg: "#4a1e8a", label: "PRICE UPLOAD" },
  CREATE_PROJECT_MEMBER: { bg: "#1f7a5a", label: "ADD MEMBER" },
  DELETE_PROJECT_MEMBER: { bg: "#8a3c5f", label: "REM MEMBER" },
};
const DEFAULT_ACTION = { bg: "#445", label: null };

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ka-GE", {
      month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_COLOR = {
  active:    "#22c55e",
  draft:     "#f59e0b",
  completed: "#3b82f6",
  archived:  "#6b7280",
  pending:   "#a78bfa",
  cancelled: "#ef4444",
};
const STATUS_LABEL = {
  active:    "აქტიური",
  draft:     "მუშავდება",
  completed: "დასრულებული",
  archived:  "არქივი",
  pending:   "მოლოდინში",
  cancelled: "გაუქმებული",
};

function sColor(s) { return STATUS_COLOR[(s || "").toLowerCase()] || "#94a3b8"; }
function sLabel(s) { return STATUS_LABEL[(s || "").toLowerCase()] || s || "—"; }

export default function AdminPanelPage({ projects, customers, isSuperuser, token, onOpenProject, onBack }) {
  const [search,        setSearch]       = useState("");
  const [statusFilter,  setStatusFilter] = useState("all");
  const [sortBy,        setSortBy]       = useState("date");
  const [activityLogs,  setActivityLogs] = useState([]);
  const [activityError, setActivityError] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchActivity = useCallback(async () => {
    if (!token) return;
    setActivityLoading(true);
    try {
      const res = await fetch(apiUrl("activity-logs?limit=8"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActivityLogs(Array.isArray(data) ? data : []);
      setActivityError(null);
    } catch (e) {
      setActivityError(e.message);
    } finally {
      setActivityLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const customerMap = useMemo(() => {
    const m = {};
    (customers || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [customers]);

  const statuses = useMemo(() => {
    const s = new Set(
      (projects || []).map((p) => (p.status || "").toLowerCase()).filter(Boolean)
    );
    return Array.from(s);
  }, [projects]);

  const stats = useMemo(() => {
    const byStatus = {};
    (projects || []).forEach((p) => {
      const s = (p.status || "").toLowerCase();
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return { total: (projects || []).length, byStatus, customers: (customers || []).length };
  }, [projects, customers]);

  const filtered = useMemo(() => {
    let list = [...(projects || [])];
    if (statusFilter !== "all") {
      list = list.filter((p) => (p.status || "").toLowerCase() === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (customerMap[p.customer_id] || "").toLowerCase().includes(q) ||
        String(p.id).includes(q) ||
        (p.location || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
      return new Date(b.create_date || 0) - new Date(a.create_date || 0);
    });
    return list;
  }, [projects, search, statusFilter, sortBy, customerMap]);

  return (
    <div
      style={{ minHeight: "100vh", background: "var(--page-gradient)", color: "var(--text-primary)", padding: "20px 16px" }}
      data-no-translate="true"
    >
      <button className="link" onClick={onBack}>← უკან</button>

      {/* ── Header ── */}
      <div style={{ marginTop: 16, marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>მართვის პანელი</h2>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
          {isSuperuser ? "სუპერ ადმინი · ყველა პროექტი" : "ადმინი · თქვენი პროექტები"}
        </div>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
        <StatBox label="სულ პროექტი" value={stats.total} color="#06b6d4"
          onClick={() => setStatusFilter("all")} active={statusFilter === "all"} />
        <StatBox label="კლიენტი" value={stats.customers} color="#f59e0b" />
        {statuses.slice(0, 1).map((s) => (
          <StatBox key={s} label={sLabel(s)} value={stats.byStatus[s] || 0} color={sColor(s)}
            onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
            active={statusFilter === s} />
        ))}
      </div>

      {/* Status filter chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
            style={{
              fontSize: 11, padding: "3px 10px", borderRadius: 10, cursor: "pointer",
              background: statusFilter === s ? `${sColor(s)}25` : "var(--surface-soft)",
              color: statusFilter === s ? sColor(s) : "var(--text-muted)",
              border: `1px solid ${statusFilter === s ? sColor(s) + "60" : "rgba(148,163,184,0.2)"}`,
              transition: "all 0.15s",
            }}
          >
            {sLabel(s)} · {stats.byStatus[s] || 0}
          </button>
        ))}
      </div>

      {/* ── Activity Feed ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6 }}>
            ბოლო აქტივობა
          </div>
          <button onClick={fetchActivity} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 11, color: "var(--text-muted)", padding: "1px 4px",
          }}>↻</button>
        </div>

        {activityLoading && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "6px 0" }}>იტვირთება...</div>
        )}
        {!activityLoading && activityError && (
          <div style={{ fontSize: 11, color: "#f87171", padding: "6px 0" }}>
            {activityError}
          </div>
        )}
        {!activityLoading && !activityError && activityLogs.length === 0 && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "6px 0" }}>ჩანაწერები არ არის</div>
        )}
        {activityLogs.map((log) => {
          const { bg, label } = ACTION_STYLES[log.action] || DEFAULT_ACTION;
          return (
            <div key={log.id} style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              padding: "6px 10px", marginBottom: 4,
              background: "var(--surface-soft)",
              border: "1px solid rgba(148,163,184,0.1)",
              borderRadius: 6,
            }}>
              <span style={{
                fontSize: 9, fontWeight: 700, color: "#fff",
                background: bg, borderRadius: 3,
                padding: "2px 6px", flexShrink: 0, letterSpacing: 0.3,
                whiteSpace: "nowrap", marginTop: 1,
              }}>
                {label || log.action}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {log.user_name || (log.user_id ? `#${log.user_id}` : "—")}
                  {log.detail ? (
                    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · {log.detail}</span>
                  ) : null}
                </div>
              </div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0, whiteSpace: "nowrap" }}>
                {fmtTime(log.created_at)}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Search + sort ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input
          type="text"
          placeholder="სახელი, კლიენტი, ადგილმდებარეობა..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, padding: "7px 10px", borderRadius: 6, fontSize: 12,
            border: "1px solid rgba(148,163,184,0.3)",
            background: "var(--surface-soft)", color: "var(--text-primary)",
          }}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: "6px 8px", borderRadius: 6, fontSize: 11,
            border: "1px solid rgba(148,163,184,0.3)",
            background: "var(--surface-soft)", color: "var(--text-primary)",
          }}
        >
          <option value="date">თარიღი</option>
          <option value="name">სახელი</option>
          <option value="status">სტატუსი</option>
        </select>
      </div>

      {/* ── Count ── */}
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
        {filtered.length} პროექტი{statusFilter !== "all" ? ` · ${sLabel(statusFilter)}` : ""}
      </div>

      {/* ── Project list ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: 13, padding: "24px 0", textAlign: "center" }}>
            პროექტები ვერ მოიძებნა
          </div>
        ) : (
          filtered.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              customerName={customerMap[p.customer_id]}
              onClick={() => onOpenProject(p.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, color, onClick, active }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? `${color}20` : "var(--surface-soft)",
        border: `1px solid ${active ? color + "60" : "rgba(148,163,184,0.15)"}`,
        borderRadius: 10, padding: "12px 8px",
        textAlign: "center", cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s",
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>{label}</div>
    </button>
  );
}

function ProjectRow({ project, customerName, onClick }) {
  const color   = sColor(project.status);
  const label   = sLabel(project.status);
  const dateStr = project.create_date
    ? new Date(project.create_date).toLocaleDateString("ka-GE", { year: "numeric", month: "2-digit", day: "2-digit" })
    : null;

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", padding: "10px 12px",
        background: "var(--surface-soft)",
        border: "1px solid rgba(148,163,184,0.15)",
        borderRadius: 8, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10,
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(148,163,184,0.4)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(148,163,184,0.15)")}
    >
      {/* ID badge */}
      <div style={{
        fontSize: 10, fontWeight: 700, color: "#94a3b8",
        background: "rgba(148,163,184,0.1)", borderRadius: 4,
        padding: "2px 5px", flexShrink: 0,
      }}>
        #{project.id}
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: "var(--text-primary)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {project.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {customerName && <span>{customerName}</span>}
          {project.location && <span>{project.location}</span>}
          {dateStr && <span style={{ marginLeft: "auto" }}>{dateStr}</span>}
        </div>
      </div>

      {/* Status badge */}
      <span style={{
        fontSize: 10, padding: "2px 8px", borderRadius: 10, flexShrink: 0,
        background: `${color}20`, color, border: `1px solid ${color}50`,
        whiteSpace: "nowrap",
      }}>
        {label}
      </span>
    </button>
  );
}
